import { cn } from "@babackapp/customer-ui/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { motion } from "motion/react";
import { AnimatedList, AnimatedListItem } from "./animated-list";

export function OptionsList({
  className,
  ...props
}: React.ComponentProps<typeof AnimatedList>) {
  return <AnimatedList className={cn("group/options", className)} {...props} />;
}

export function Option({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <AnimatedListItem>
      <div
        className={cn(
          "cursor-pointer group/option border p-4 rounded-lg has-[[data-state=checked]]:border-primary w-full bg-background",
          // ok crazy but actually okish: if the grouphas a checked item && if the current item is not checked, lower opacity
          "group-has-[[data-state=checked]]/options:[&:not(:has([data-state=checked]))]:opacity-60",
          "group-has-[[data-state=checked]]/options:[&:not(:has([data-state=checked]))]:scale-[0.98]",
          "transition-[scale,opacity] ease-in-out duration-200",
          "grid grid-cols-[auto_1fr_auto]",
          "gap-x-3 gap-y-1",
          className,
        )}
        {...props}
      />
    </AnimatedListItem>
  );
}

export function OptionIcon({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "row-start-1 col-start-1 row-span-2",
        "[&_svg]:size-5 [&_svg]:translate-y-0.5",
        className,
      )}
      {...props}
    />
  );
}

export function OptionContent({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "col-start-1 row-start-3 row-span-2 border-t col-span-3 pt-3 mt-2 flex gap-2 items-center",
        className,
      )}
      {...props}
    />
  );
}

export function OptionTitle({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      className={cn(
        // min-w-0: a grid item's default min-width is its content's min-content
        // size, so without this the title (label + badges) forces the "1fr"
        // column wider than the space actually available, pushing the price +
        // radio in the "auto" column past the card's right edge instead of
        // wrapping. flex-wrap lets label and badges drop to a second line
        // once that happens, rather than overflowing sideways.
        "row-start-1 col-start-2 min-w-0",
        "font-sans font-medium text-base text-foreground inline-flex flex-wrap gap-2 items-center",
        className,
      )}
      {...props}
    />
  );
}

export function OptionDescription({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      className={cn(
        "row-start-2 col-start-2",
        "font-content text-sm text-balance text-muted-foreground md:text-pretty",
        className,
      )}
      {...props}
    />
  );
}

export function OptionAction({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "row-start-1 col-start-3 row-span-2",
        "flex h-full items-center justify-center [&_svg]:size-4",
        className,
      )}
      {...props}
    />
  );
}

export function OptionFooter({
  className,
  asChild,
  ...props
}: React.ComponentProps<"div"> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "div";

  return (
    <Comp
      className={cn(
        "row-start-3 col-start-1 col-span-3 gap-1.5 -mb-2",
        className,
      )}
      {...props}
    />
  );
}

export function OptionFooterBanner({
  className,
  ...props
}: React.ComponentProps<"div"> & { asChild?: boolean }) {
  const Comp = props.asChild ? Slot : "div";

  return (
    <Comp
      className={cn(
        "flex items-center gap-1.5 w-full rounded-sm ",
        "bg-info font-content border-info-border border rounded-sm py-1 px-2.5 text-sm text-info-foreground",
        "[&_svg]:text-info-foreground",
        className,
      )}
      {...props}
    />
  );
}
